import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useBibleStore } from '../../../shared/store/bibleStore';
import { PageHeader } from '../../../shared/components/layout/pageHeader';
import { Header } from '../../../shared/components/layout/Header';
import { OLD_TESTAMENT, NEW_TESTAMENT } from '../../bible/utils/bibleBooks';
import { BookmarkVersesCarousel } from './BookmarkVersesCarousel';

interface Bookmark {
  id: string;
  book: string;
  chapter: number;
  verse: number;
  created_at: string;
}

interface BookGroup {
  book: string;
  bookmarks: Bookmark[];
}

export default function Bookmarks() {
  const { user } = useAuth();
  const { bookmarks, isLoading, loadBookmarks } = useBibleStore();

  useEffect(() => {
    if (user) {
      loadBookmarks();
    }
  }, [user]);

  const groupedBookmarks = useMemo(() => {
    // Get all books in biblical order
    const allBooks = [
      ...Object.values(OLD_TESTAMENT).flat(),
      ...Object.values(NEW_TESTAMENT).flat()
    ];

    // Create a map of book to bookmarks
    const bookGroups = bookmarks.reduce((groups, bookmark) => {
      // Find the matching book name (case-insensitive)
      const bookName = allBooks.find(
        book => book.toLowerCase() === bookmark.book.toLowerCase()
      ) || bookmark.book;

      if (!groups[bookName]) {
        groups[bookName] = [];
      }
      groups[bookName].push(bookmark);
      return groups;
    }, {} as Record<string, Bookmark[]>);

    // Sort bookmarks within each book by chapter and verse
    Object.values(bookGroups).forEach(bookmarkList => {
      bookmarkList.sort((a, b) => {
        if (a.chapter !== b.chapter) {
          return a.chapter - b.chapter;
        }
        return a.verse - b.verse;
      });
    });

    // Return all groups
    return Object.entries(bookGroups).map(([book, bookmarks]) => ({
      book,
      bookmarks
    }));
  }, [bookmarks]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-12 mt-[60px]">
        <PageHeader
          headline="Your Bookmarks"
          subheadline="Access your saved verses and insights"
        />

        {bookmarks.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No bookmarks yet</p>
          </div>
        ) : (
          <div className="space-y-12">
            {groupedBookmarks.map(({ book, bookmarks }) => (
              <BookmarkVersesCarousel
                key={book}
                bookName={book}
                bookmarks={bookmarks}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

